// MARK://dhk8/pos/features/floor/usePanZoom
// Pointer-driven pan + zoom for the floor canvas. Works with mouse and touch (a single
// PointerEvent pipeline + a 2-finger pinch path). The transform is applied as
// `translate(tx,ty) scale(scale)` on the inner canvas layer; zoom is clamped 0.5×–2×.
// `touch-action: none` on the host element is required so the browser doesn't steal the
// gesture for page scroll while panning.
import { useCallback, useEffect, useRef, useState } from 'react';
// MIN_SCALE bajado de 0.5 → 0.2: viewports estrechos del POS (~535px) con
// planos de 1200×800 necesitan ~0.44 para entrar; al clampar a 0.5 el canvas
// sobre-llenaba el viewport y las mesas en los bordes quedaban fuera del
// hit-area, devolviendo MAIN al elementFromPoint. 0.2 es suficiente para
// cualquier tablet y todavía deja al cajero zoom-in con +/− buttons.
const MIN_SCALE = 0.2;
const MAX_SCALE = 2;
function clampScale(s) {
    const n = Math.max(MIN_SCALE, Math.min(MAX_SCALE, s));
    return Number.isFinite(n) ? n : 1;
}
// Pointer-capture calls can throw (InvalidPointerId / InvalidStateError) when a
// pointer is already gone — common under rapid multi-touch on iOS Safari. Never
// let that bubble into React's error boundary; the gesture just degrades to
// uncaptured pointer events, which still work.
function capturePointer(el, id) {
    try {
        el?.setPointerCapture?.(id);
    }
    catch { /* pointer already released */ }
}
function releasePointer(el, id) {
    try {
        el?.releasePointerCapture?.(id);
    }
    catch { /* never captured */ }
}
function safeRect(el) {
    const r = el?.getBoundingClientRect?.();
    return r ? { left: r.left, top: r.top, width: r.width, height: r.height } : { left: 0, top: 0, width: 0, height: 0 };
}
export function usePanZoom(opts) {
    const { contentW, contentH, getViewport, shouldStartPan } = opts;
    const [transform, setTransform] = useState({ tx: 0, ty: 0, scale: 1 });
    const [panning, setPanning] = useState(false);
    // Active pointers, by pointerId → last client position.
    const pointers = useRef(new Map());
    const panStart = useRef(null);
    const pinchStart = useRef(null);
    const fittedOnce = useRef(false);
    const computeFit = useCallback(() => {
        const vp = getViewport();
        if (!vp || vp.width === 0 || vp.height === 0)
            return { tx: 0, ty: 0, scale: 1 };
        // 5% margin (0.95) so el plano respira en cualquier viewport. Importante:
        // el fit NO aplica `clampScale` — la intención del usuario al pulsar
        // "Ajustar" es ver todo el canvas, así que permitimos bajar por debajo
        // de MIN_SCALE si el viewport es muy chico. El clamp sólo se aplica al
        // zoom manual (botones +/− y pinch) para evitar pixelado extremo.
        const FIT_MARGIN = 0.95;
        const FIT_MIN = 0.05;
        const raw = Math.min(vp.width / contentW, vp.height / contentH) * FIT_MARGIN;
        const scale = Math.max(FIT_MIN, Math.min(MAX_SCALE, raw));
        return {
            scale,
            tx: (vp.width - contentW * scale) / 2,
            ty: (vp.height - contentH * scale) / 2,
        };
    }, [contentW, contentH, getViewport]);
    const fit = useCallback(() => {
        setTransform(computeFit());
    }, [computeFit]);
    // Fit once on mount (after layout settles).
    useEffect(() => {
        if (fittedOnce.current)
            return;
        const id = requestAnimationFrame(() => {
            fittedOnce.current = true;
            setTransform(computeFit());
        });
        return () => cancelAnimationFrame(id);
    }, [computeFit]);
    const zoomAt = useCallback((factor, clientX, clientY, hostRect) => {
        setTransform((t) => {
            const nextScale = clampScale(t.scale * factor);
            const f = nextScale / t.scale;
            // Keep the point under the cursor fixed.
            const ox = hostRect ? clientX - hostRect.left : clientX;
            const oy = hostRect ? clientY - hostRect.top : clientY;
            return {
                scale: nextScale,
                tx: ox - (ox - t.tx) * f,
                ty: oy - (oy - t.ty) * f,
            };
        });
    }, []);
    const zoomBy = useCallback((factor) => {
        const vp = getViewport();
        if (!vp)
            return;
        zoomAt(factor, vp.width / 2, vp.height / 2);
    }, [getViewport, zoomAt]);
    const onPointerDown = useCallback((e) => {
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointers.current.size === 2) {
            // Begin pinch.
            const pts = [...pointers.current.values()];
            const a = pts[0];
            const b = pts[1];
            const dist = Math.hypot(a.x - b.x, a.y - b.y);
            const rect = safeRect(e.currentTarget);
            pinchStart.current = {
                dist,
                scale: transform.scale,
                cx: (a.x + b.x) / 2 - rect.left,
                cy: (a.y + b.y) / 2 - rect.top,
                tx: transform.tx,
                ty: transform.ty,
            };
            panStart.current = null;
            setPanning(false);
            return;
        }
        if (pointers.current.size > 1)
            return;
        // Single pointer: only start a pan if the target is empty canvas space.
        if (shouldStartPan && !shouldStartPan(e.target))
            return;
        capturePointer(e.currentTarget, e.pointerId);
        panStart.current = { x: e.clientX, y: e.clientY, tx: transform.tx, ty: transform.ty };
        setPanning(true);
    }, [transform, shouldStartPan]);
    const onPointerMove = useCallback((e) => {
        if (!pointers.current.has(e.pointerId))
            return;
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pinchStart.current && pointers.current.size >= 2) {
            const pts = [...pointers.current.values()];
            const a = pts[0];
            const b = pts[1];
            const dist = Math.hypot(a.x - b.x, a.y - b.y);
            const ps = pinchStart.current;
            const nextScale = clampScale(ps.scale * (dist / (ps.dist || 1)));
            const f = nextScale / ps.scale;
            setTransform({
                scale: nextScale,
                tx: ps.cx - (ps.cx - ps.tx) * f,
                ty: ps.cy - (ps.cy - ps.ty) * f,
            });
            return;
        }
        if (panStart.current) {
            // Snapshot panStart.current AHORA. React puede correr el setState reducer
            // de forma asíncrona (commit phase) y entre tanto un pointerup/cancel
            // pudo haber nulificado panStart.current — el `!.tx` reventaba (#drag-bug).
            const ps = panStart.current;
            const dx = e.clientX - ps.x;
            const dy = e.clientY - ps.y;
            const baseTx = ps.tx;
            const baseTy = ps.ty;
            setTransform((t) => ({ ...t, tx: baseTx + dx, ty: baseTy + dy }));
        }
    }, []);
    const endPointer = useCallback((e) => {
        pointers.current.delete(e.pointerId);
        if (pointers.current.size < 2)
            pinchStart.current = null;
        if (pointers.current.size === 0) {
            panStart.current = null;
            setPanning(false);
        }
        else if (panStart.current) {
            // A finger lifted while >1 pointer remains (e.g. pinch → pan transition):
            // re-anchor the pan to a still-active pointer so we don't jump.
            const next = [...pointers.current.entries()][0];
            if (next)
                panStart.current = { x: next[1].x, y: next[1].y, tx: transform.tx, ty: transform.ty };
        }
        releasePointer(e.currentTarget, e.pointerId);
    }, [transform]);
    const onWheel = useCallback((e) => {
        e.preventDefault();
        const r = safeRect(e.currentTarget);
        const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
        zoomAt(factor, e.clientX, e.clientY, r);
    }, [zoomAt]);
    return {
        transform,
        hostHandlers: { onPointerDown, onPointerMove, onPointerUp: endPointer, onPointerCancel: endPointer, onWheel },
        panning,
        fit,
        zoomBy,
    };
}
//# sourceMappingURL=usePanZoom.js.map